// The newsletter preview, in the browser.
//
// Wave 13 (session 46). This is the MIRROR of tools/lotus_newsletter.py on the
// droplet: the same colour derivation (derivePalette <-> palette) and the same
// little template language (render <-> render). The droplet builds the real
// PDF; the portal only shows a client what their template will look like with
// their logo, colours and font before they save. scripts/session46_test_newsletter.py
// runs both against the same inputs and fails if they drift.
//
// KEEP IN STEP with tools/lotus_newsletter.py. If you change one, change the other.
//
// Session 53 (Wave 15a): the templates carry {{#mc}}...{{/mc}} sections, the
// Mailchimp-only parts (mc:edit regions, the *|UNSUB|* footer line). The
// preview never sets `mc`, so render() drops them, exactly as the PDF does.

export const TEMPLATES = [
  ['bold', 'Bold', 'Big headline on a block of your main colour, numbered points, a bright call to action.'],
  ['editorial', 'Editorial', 'Magazine feature: picture first, a pull quote, a byline and read time.'],
  ['elegant', 'Elegant', 'Centred and quiet, with a serif headline and fine rules.'],
  ['modern', 'Modern', 'Clean and friendly, all sans-serif, rounded corners.'],
];

export const FONTS = [
  ['', 'No preference (the template’s own)'],
  ['georgia', 'Georgia, a classic serif'],
  ['arial', 'Arial, clean and plain'],
  ['trebuchet', 'Trebuchet, friendly and rounded'],
  ['verdana', 'Verdana, wide and sturdy'],
];

const FONT_STACKS = {
  georgia: "Georgia,'Times New Roman',serif",
  arial: 'Arial,Helvetica,sans-serif',
  trebuchet: "'Trebuchet MS',Arial,sans-serif",
  verdana: 'Verdana,Geneva,sans-serif',
};
const TEMPLATE_HEADLINE_FONT = {
  bold: "'Arial Black',Arial,sans-serif",
  editorial: "Georgia,'Times New Roman',serif",
  elegant: "Georgia,'Times New Roman',serif",
  modern: "'Trebuchet MS',Arial,sans-serif",
};

const INK = '#12232B';
const WHITE = '#FFFFFF';

export function fontStack(choice, template) {
  return FONT_STACKS[String(choice || '').trim().toLowerCase()] || TEMPLATE_HEADLINE_FONT[template];
}

/* ------------------------------------------------------------- colours */

function hexToRgb(h) {
  let s = String(h || '').trim().replace(/^#/, '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(s)) throw new Error(`not a colour: ${h}`);
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}
const rgbToHex = (rgb) => '#' + rgb.map((c) => Math.max(0, Math.min(255, Math.trunc(c))).toString(16).padStart(2, '0').toUpperCase()).join('');
const roundHalfUp = (x) => Math.floor(x + 0.5);

function mix(a, b, t) {
  const ra = hexToRgb(a); const rb = hexToRgb(b);
  return rgbToHex([0, 1, 2].map((i) => roundHalfUp(ra[i] * (1 - t) + rb[i] * t)));
}

function luminance(h) {
  const lin = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const [r, g, b] = hexToRgb(h).map(lin);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a, b) {
  const la = luminance(a); const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// White or ink, whichever reads better on the block. Computed, never assumed.
const on = (bg) => (contrast(bg, WHITE) >= contrast(bg, INK) ? WHITE : INK);

function textOnWhite(c) {
  let d = c.toUpperCase();
  for (let i = 0; i < 20; i += 1) {
    if (contrast(d, WHITE) >= 4.5) break;
    d = mix(d, '#000000', 0.08);
  }
  return d;
}

export function isHex(h) {
  try { hexToRgb(h); return true; } catch { return false; }
}

export function normalizeHex(h, fallback) {
  try { return rgbToHex(hexToRgb(h)); } catch { return fallback; }
}

// The quieter text colour on a block: `on` pulled t of the way toward the
// block, but never below 4.5:1 against it when `on` itself manages that.
function soft(onC, bg, t0) {
  const limit = Math.min(4.5, contrast(onC, bg));
  let t = t0;
  while (t > 0) {
    const c = mix(onC, bg, t);
    if (contrast(c, bg) >= limit) return c;
    t = Math.round((t - 0.05) * 100) / 100;
  }
  return onC;
}

export function derivePalette(mainIn, accentIn) {
  const main = rgbToHex(hexToRgb(mainIn));
  const accent = rgbToHex(hexToRgb(accentIn));
  const mainDeep = mix(main, '#000000', 0.3);
  const onMain = on(main); const onMainDeep = on(mainDeep); const onAccent = on(accent);
  const accentText = textOnWhite(accent);
  return {
    c_main: main,
    c_accent: accent,
    c_main_text: textOnWhite(main),
    c_accent_text: accentText,
    c_main_deep: mainDeep,
    c_main_tint: mix(main, WHITE, 0.82),
    c_main_tint2: mix(main, WHITE, 0.55),
    c_on_main: onMain,
    c_on_main_soft: soft(onMain, main, 0.2),
    c_on_main_deep: onMainDeep,
    c_on_main_deep_soft: soft(onMainDeep, mainDeep, 0.3),
    c_on_accent: onAccent,
    c_on_accent_inv: onAccent === INK ? WHITE : INK,
    c_accent_tint: mix(accent, WHITE, 0.9),
    c_btn_on_main_text: onMain === WHITE ? accentText : WHITE,
  };
}

/* ------------------------------------------------------- the template */

const SECTION_RE = /\{\{([#^])(\w+)\}\}([\s\S]*?)\{\{\/\2\}\}/g;
const VAR_RE = /\{\{(\w+)\}\}/g;
const truthy = (v) => !(v === null || v === undefined || v === '' || v === false || v === 0
  || (Array.isArray(v) && v.length === 0));
// & < > and the double quote: every attribute in the templates is double-quoted,
// so an apostrophe (in a title, or in a font stack like 'Arial Black') stays.
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

export function render(template, slots) {
  let out = template;
  for (let i = 0; i < 50; i += 1) {
    const next = out.replace(SECTION_RE, (m, kind, key, body) => (truthy(slots[key]) === (kind === '#') ? body : ''));
    if (next === out) break;
    out = next;
  }
  return out.replace(VAR_RE, (m, key) => (slots[key] === undefined || slots[key] === null ? '' : esc(slots[key])));
}

export function derivedSlots(slots) {
  const s = { ...slots };
  const overview = String(s.overview || '').trim();
  s.overview_first = overview.slice(0, 1); s.overview_rest = overview.slice(1);
  const words = String(s.title || '').split(/\s+/).filter(Boolean);
  if (words.length >= 4) { s.title_main = words.slice(0, -2).join(' '); s.title_tail = words.slice(-2).join(' '); }
  else { s.title_main = words.join(' '); s.title_tail = ''; }
  const cw = String(s.cta_heading || '').split(/\s+/).filter(Boolean);
  if (cw.length >= 3) { s.cta_heading_main = cw.slice(0, -2).join(' '); s.cta_heading_tail = cw.slice(-2).join(' '); }
  else { s.cta_heading_main = cw.join(' '); s.cta_heading_tail = ''; }
  s.contact_line = ['city', 'phone'].map((k) => String(s[k] || '').trim()).filter(Boolean).join(' · ');
  s.email_website_sep = Boolean(s.email) && Boolean(s.website_url);
  s.phone_email_sep = Boolean(s.phone) && Boolean(s.email);
  const url = String(s.website_url || '').trim();
  // (lower-cased: a website typed as "Digitallotusmarketing.com" is still a host)
  s.website_label = url ? url.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase() : '';
  return s;
}

/* ------------------------------------------- the preview a client sees */

// `sample` is public/newsletter/sample.json; `settings` is the form state;
// `business` is {name, website}. Everything typed by the client is escaped
// by render(), so nothing they enter can become markup in their own preview.
export function previewSlots(sample, template, settings, business, { useSample = false } = {}) {
  const b = sample.brand; const base = sample.slots;
  const main = normalizeHex(settings.main_color, b.main_color);
  const accent = normalizeHex(settings.accent_color, b.accent_color);
  const website = String(business?.website || '').trim();
  const websiteUrl = website && !/^https?:\/\//i.test(website) ? `https://${website}` : website;
  const s = {
    ...base,
    business_name: useSample ? base.business_name : (business?.name || base.business_name),
    tagline: useSample ? base.tagline : (settings.footer?.tagline || ''),
    city: useSample ? base.city : (settings.footer?.city || ''),
    phone: useSample ? base.phone : (settings.footer?.phone || ''),
    email: useSample ? base.email : (settings.footer?.email || ''),
    website_url: useSample ? base.website_url : (websiteUrl || ''),
    read_more_url: useSample ? base.read_more_url : (websiteUrl || base.read_more_url),
    cta_heading: useSample ? base.cta_heading : (settings.cta?.heading || 'Looking for more help?'),
    cta_line: useSample ? base.cta_line : (settings.cta?.line || ''),
    cta_button_text: useSample ? base.cta_button_text : (settings.cta?.button_text || 'Visit our website'),
    cta_button_url: useSample ? base.cta_button_url : (settings.cta?.button_url || websiteUrl || '#'),
    hero_src: `/newsletter/${b.hero}`,
    logo_src: useSample ? `/${b.logo}` : (settings.logo_preview_url || ''),
    f_head: fontStack(useSample ? b.headline_font : settings.headline_font, template),
    ...derivePalette(main, accent),
  };
  return derivedSlots(s);
}

export async function loadTemplate(template) {
  const r = await fetch(`/newsletter/templates/${template}.html`);
  if (!r.ok) throw new Error(`Could not load the ${template} template`);
  return r.text();
}

export async function loadSample() {
  const r = await fetch('/newsletter/sample.json');
  if (!r.ok) throw new Error('Could not load the sample newsletter');
  return r.json();
}
